'use client';

import { useMemo, useRef } from 'react';

import { useCosmicModule } from '../../hooks/useCosmicModule';
import { Deal } from '../../hooks/useDeal';
import { VisibilityConfig } from '../../types';
import { PlatformComponentType } from '../../types/wizard';
import { FeatureFormApi, Field } from '../FeatureForm';
import { ModalBuilder } from '../ModalBuilder';
import { ModalProps } from '../ModalBuilder/components/Modal';
import { StepWithOptionalSlug, WizardRef } from '../Wizard';
import {
  UniversalContentItem,
  UniversalContentItemProps,
} from './components/UniversalContentItem';

export type ExtraStep = {
  id?: string;
  type?: 'replace' | 'insert' | 'delete';
  slug?: string;
  position: number;
  step?: (params: any) => StepWithOptionalSlug;
};

export type ModuleProps = {
  PlatformComponent: PlatformComponentType;
  extraSteps?: ExtraStep[];
  onSubmit?: (data: Record<string, any>) => Promise<void>;
};

type MultiStepModalProps = ModalProps &
  ModuleProps & {
    subscription?: Deal;
    moduleName: string;
  };

export type Content = {
  contact_autocomplete?: {
    extraFields?: Field[];
    labels?: Record<string, string>;
  };
  address_autocomplete?: {
    extraFields?: Field[];
    labels?: Record<string, string>;
  };
  steps: {
    metadata: {
      slug?: string;
      hidden?: VisibilityConfig;
      content?: UniversalContentItemProps[];
      fields: Field[];
    };
  }[];
};

export const MultiStepModal = (props: MultiStepModalProps) => {
  const formApiRef = useRef<FeatureFormApi>(null);
  const wizardRef = useRef<WizardRef>(null);
  const content: Content = useCosmicModule(props.moduleName);

  const steps = useMemo(() => {
    const nextSteps = content?.steps
      ?.filter((step) => step)
      .map((step) => ({
        ...step,
        metadata: {
          ...step.metadata,
          renderAfterContent: () =>
            step.metadata?.content?.map((contentItem, index) => (
              <UniversalContentItem key={index} {...contentItem} />
            )),
          isCTADisabled: (errors: Record<string, any>) =>
            step.metadata.fields?.some((field) => Boolean(errors[field.name])),
        },
        extraFieldsToValidate: step.metadata?.content?.filter(
          (item) => item.type === 'boolean'
        ) as Field[],
        fields: step.metadata.fields,
      }));

    if (props?.extraSteps?.length && nextSteps?.length) {
      props.extraSteps.forEach((extraStep) => {
        if (extraStep?.step) {
          nextSteps.splice(
            extraStep.position,
            extraStep.type === 'replace' ? 1 : 0,
            {
              ...extraStep?.step({
                wizardRef,
              }),
            } as (typeof nextSteps)[0]
          );
        } else if (extraStep.type === 'delete') {
          nextSteps.splice(extraStep.position, 1);
        }
      });
    }

    return nextSteps;
  }, [content, props.extraSteps]);

  return (
    <ModalBuilder
      ref={wizardRef}
      formApiRef={formApiRef}
      PlatformComponent={props.PlatformComponent}
      open={props.open}
      onFormSubmit={props.onSubmit}
      onClose={props.onClose}
      steps={steps}
      location={props.name}
    />
  );
};
